import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  TrendingUp, DollarSign, Calculator, Target,
  Package, RefreshCw, ChevronDown, ChevronUp,
  AlertCircle, BarChart3, PieChart
} from 'lucide-react';

// Current package pricing from mockData
const PACKAGES = {
  services: [
    { id: 'diagnostic-review', name: 'Diagnostic Bundle Review', price: 495, type: 'one-time' },
    { id: 'advocacy-launch', name: 'Advocacy Launch Session', price: 1197, type: 'one-time' },
    { id: 'deep-dive', name: 'Deep Dive Consultation', price: 1797, type: 'one-time' }
  ],
  memberships: [
    { id: 'navigator', name: 'Navigator Membership', price: 179, type: 'monthly' },
    { id: 'advocate', name: 'Advocate Membership', price: 399, type: 'monthly' },
    { id: 'concierge', name: 'Concierge Membership', price: 799, type: 'monthly' }
  ]
};

const PackagePrediction = ({ expenseSummary }) => {
  const { token } = useAuth();
  const [expanded, setExpanded] = useState(true);
  const [scenarios, setScenarios] = useState([]);
  const [customScenario, setCustomScenario] = useState({
    packageId: 'advocacy-launch',
    packageType: 'services',
    monthlySales: 5,
    priceChange: 0
  });
  const [breakEvenAnalysis, setBreakEvenAnalysis] = useState(null);

  // Calculate break-even analysis based on expenses
  const calculateBreakEven = useCallback(() => {
    if (!expenseSummary) return;

    const monthlyExpenses = expenseSummary.monthly_burn_rate || 0;
    const totalInvested = expenseSummary.total_invested || 0;

    // Calculate break-even for each package
    const breakEvens = [];
    
    // Services (one-time)
    PACKAGES.services.forEach(pkg => {
      const salesNeededMonthly = monthlyExpenses > 0 ? Math.ceil(monthlyExpenses / pkg.price) : 0;
      const salesNeededToRecoverInvestment = totalInvested > 0 ? Math.ceil(totalInvested / pkg.price) : 0;
      
      breakEvens.push({
        ...pkg,
        salesNeededMonthly,
        salesNeededToRecoverInvestment,
        monthsToRecoverAt5Sales: totalInvested > 0 ? Math.ceil(totalInvested / (pkg.price * 5)) : 0
      });
    });

    // Memberships (monthly recurring)
    PACKAGES.memberships.forEach(pkg => {
      const membersNeededForBreakEven = monthlyExpenses > 0 ? Math.ceil(monthlyExpenses / pkg.price) : 0;
      const monthsToRecover = totalInvested > 0 && pkg.price > 0 
        ? Math.ceil(totalInvested / (pkg.price * Math.max(1, membersNeededForBreakEven)))
        : 0;
      
      breakEvens.push({
        ...pkg,
        membersNeeded: membersNeededForBreakEven,
        monthsToRecover,
        annualRevenuePerMember: pkg.price * 12
      });
    });

    setBreakEvenAnalysis({
      monthlyExpenses,
      totalInvested,
      packages: breakEvens
    });
  }, [expenseSummary]);

  useEffect(() => {
    calculateBreakEven();
  }, [calculateBreakEven]);

  // Generate prediction scenarios
  const generateScenarios = useCallback(() => {
    const selected = customScenario.packageType === 'services' 
      ? PACKAGES.services.find(p => p.id === customScenario.packageId)
      : PACKAGES.memberships.find(p => p.id === customScenario.packageId);
    
    if (!selected) return;

    const basePrice = selected.price;
    const adjustedPrice = basePrice * (1 + customScenario.priceChange / 100);
    const monthlySales = customScenario.monthlySales;

    const monthlyExpenses = expenseSummary?.monthly_burn_rate || 500;
    const totalInvested = expenseSummary?.total_invested || 5000;

    // Generate 12-month projection
    const newScenarios = [];
    
    if (selected.type === 'one-time') {
      // One-time service projections
      for (let month = 1; month <= 12; month++) {
        const cumulativeSales = monthlySales * month;
        const grossRevenue = adjustedPrice * cumulativeSales;
        const cumulativeExpenses = monthlyExpenses * month;
        const netProfit = grossRevenue - cumulativeExpenses - totalInvested;
        
        newScenarios.push({
          month,
          sales: cumulativeSales,
          grossRevenue,
          expenses: cumulativeExpenses + totalInvested,
          netProfit,
          isProfitable: netProfit > 0
        });
      }
    } else {
      // Membership (recurring) projections
      let totalMembers = 0;
      for (let month = 1; month <= 12; month++) {
        // Assume gradual member growth
        totalMembers += Math.ceil(monthlySales / 2);
        const monthlyRevenue = adjustedPrice * totalMembers;
        const cumulativeRevenue = newScenarios.length > 0 
          ? newScenarios[newScenarios.length - 1].grossRevenue + monthlyRevenue
          : monthlyRevenue;
        const cumulativeExpenses = monthlyExpenses * month + totalInvested;
        const netProfit = cumulativeRevenue - cumulativeExpenses;
        
        newScenarios.push({
          month,
          members: totalMembers,
          monthlyRevenue,
          grossRevenue: cumulativeRevenue,
          expenses: cumulativeExpenses,
          netProfit,
          isProfitable: netProfit > 0
        });
      }
    }

    setScenarios(newScenarios);
  }, [customScenario, expenseSummary]);

  useEffect(() => {
    generateScenarios();
  }, [generateScenarios]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const selectedPackage = customScenario.packageType === 'services'
    ? PACKAGES.services.find(p => p.id === customScenario.packageId)
    : PACKAGES.memberships.find(p => p.id === customScenario.packageId);

  const adjustedPrice = selectedPackage 
    ? selectedPackage.price * (1 + customScenario.priceChange / 100)
    : 0;

  const breakEvenMonth = scenarios.findIndex(s => s.isProfitable) + 1;

  return (
    <div className="prediction-section" data-testid="package-prediction">
      <div 
        className="prediction-header"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="prediction-title">
          <TrendingUp size={20} />
          <h3>Package Sales Prediction</h3>
        </div>
        <button className="expand-btn">
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      {expanded && (
        <div className="prediction-content">
          {/* Break-Even Analysis */}
          {breakEvenAnalysis && (
            <div className="breakeven-section">
              <h4><Target size={16} /> Break-Even Analysis</h4>
              <p className="breakeven-subtitle">
                Based on {formatCurrency(breakEvenAnalysis.monthlyExpenses)}/mo burn rate 
                and {formatCurrency(breakEvenAnalysis.totalInvested)} total invested
              </p>
              
              <div className="breakeven-grid">
                {breakEvenAnalysis.packages.filter(p => p.type === 'one-time').map(pkg => (
                  <div key={pkg.id} className="breakeven-card">
                    <div className="breakeven-card-header">
                      <Package size={16} />
                      <span>{pkg.name}</span>
                    </div>
                    <div className="breakeven-card-price">{formatCurrency(pkg.price)}</div>
                    <div className="breakeven-stats">
                      <div className="breakeven-stat">
                        <span className="stat-label">Cover Monthly Costs</span>
                        <span className="stat-value highlight">{pkg.salesNeededMonthly} sales/mo</span>
                      </div>
                      <div className="breakeven-stat">
                        <span className="stat-label">Recover Investment</span>
                        <span className="stat-value">{pkg.salesNeededToRecoverInvestment} total sales</span>
                      </div>
                      <div className="breakeven-stat">
                        <span className="stat-label">At 5 sales/mo</span>
                        <span className="stat-value">{pkg.monthsToRecoverAt5Sales} months</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <h5 className="membership-header">Membership Break-Even</h5>
              <div className="breakeven-grid memberships">
                {breakEvenAnalysis.packages.filter(p => p.type === 'monthly').map(pkg => (
                  <div key={pkg.id} className="breakeven-card membership">
                    <div className="breakeven-card-header">
                      <RefreshCw size={16} />
                      <span>{pkg.name}</span>
                    </div>
                    <div className="breakeven-card-price">{formatCurrency(pkg.price)}/mo</div>
                    <div className="breakeven-stats">
                      <div className="breakeven-stat">
                        <span className="stat-label">Members Needed</span>
                        <span className="stat-value highlight">{pkg.membersNeeded} active</span>
                      </div>
                      <div className="breakeven-stat">
                        <span className="stat-label">Annual/Member</span>
                        <span className="stat-value">{formatCurrency(pkg.annualRevenuePerMember)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Custom Scenario Builder */}
          <div className="scenario-builder">
            <h4><Calculator size={16} /> Revenue Projection Calculator</h4>
            
            <div className="scenario-inputs">
              <div className="input-group">
                <label>Package Type</label>
                <select 
                  value={customScenario.packageType}
                  onChange={(e) => {
                    const type = e.target.value;
                    setCustomScenario(prev => ({
                      ...prev,
                      packageType: type,
                      packageId: type === 'services' ? 'advocacy-launch' : 'advocate'
                    }));
                  }}
                  data-testid="package-type-select"
                >
                  <option value="services">One-Time Services</option>
                  <option value="memberships">Memberships</option>
                </select>
              </div>

              <div className="input-group">
                <label>Package</label>
                <select 
                  value={customScenario.packageId}
                  onChange={(e) => setCustomScenario(prev => ({ ...prev, packageId: e.target.value }))}
                  data-testid="package-select"
                >
                  {(customScenario.packageType === 'services' ? PACKAGES.services : PACKAGES.memberships).map(pkg => (
                    <option key={pkg.id} value={pkg.id}>
                      {pkg.name} ({formatCurrency(pkg.price)}{pkg.type === 'monthly' ? '/mo' : ''})
                    </option>
                  ))}
                </select>
              </div>

              <div className="input-group">
                <label>
                  {customScenario.packageType === 'services' ? 'Sales/Month' : 'New Members/Month'}
                </label>
                <input 
                  type="number" 
                  min="1" 
                  max="100"
                  value={customScenario.monthlySales}
                  onChange={(e) => setCustomScenario(prev => ({ 
                    ...prev, 
                    monthlySales: parseInt(e.target.value) || 1 
                  }))}
                  data-testid="monthly-sales-input"
                />
              </div>

              <div className="input-group">
                <label>Price Change (%)</label>
                <div className="price-change-input">
                  <input 
                    type="range" 
                    min="-30" 
                    max="50"
                    value={customScenario.priceChange}
                    onChange={(e) => setCustomScenario(prev => ({ 
                      ...prev, 
                      priceChange: parseInt(e.target.value) 
                    }))}
                    data-testid="price-change-slider"
                  />
                  <span className={customScenario.priceChange >= 0 ? 'positive' : 'negative'}>
                    {customScenario.priceChange >= 0 ? '+' : ''}{customScenario.priceChange}%
                  </span>
                </div>
              </div>
            </div>

            {/* Price Comparison */}
            {selectedPackage && (
              <div className="price-comparison">
                <div className="price-box current">
                  <span className="price-label">Current Price</span>
                  <span className="price-value">{formatCurrency(selectedPackage.price)}</span>
                </div>
                <div className="price-arrow">→</div>
                <div className={`price-box adjusted ${customScenario.priceChange >= 0 ? 'increase' : 'decrease'}`}>
                  <span className="price-label">Adjusted Price</span>
                  <span className="price-value">{formatCurrency(adjustedPrice)}</span>
                </div>
              </div>
            )}
          </div>

          {/* 12-Month Projection */}
          {scenarios.length > 0 && (
            <div className="projection-section">
              <h4><BarChart3 size={16} /> 12-Month Revenue Projection</h4>
              
              {breakEvenMonth > 0 && breakEvenMonth <= 12 && (
                <div className="breakeven-alert success">
                  <Target size={16} />
                  <span>Projected to break even in <strong>Month {breakEvenMonth}</strong></span>
                </div>
              )}
              
              {breakEvenMonth === 0 && (
                <div className="breakeven-alert warning">
                  <AlertCircle size={16} />
                  <span>Not projected to break even within 12 months at current rate</span>
                </div>
              )}

              <div className="projection-table-wrapper">
                <table className="projection-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>{customScenario.packageType === 'services' ? 'Cumulative Sales' : 'Total Members'}</th>
                      <th>Gross Revenue</th>
                      <th>Total Expenses</th>
                      <th>Net Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scenarios.map(s => (
                      <tr key={s.month} className={s.isProfitable ? 'profitable' : 'loss'}>
                        <td>Month {s.month}</td>
                        <td>{customScenario.packageType === 'services' ? s.sales : s.members}</td>
                        <td className="revenue">{formatCurrency(s.grossRevenue)}</td>
                        <td className="expense">{formatCurrency(s.expenses)}</td>
                        <td className={`net ${s.netProfit >= 0 ? 'positive' : 'negative'}`}>
                          {formatCurrency(s.netProfit)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary Stats */}
              <div className="projection-summary">
                <div className="summary-card">
                  <DollarSign size={20} />
                  <div>
                    <span className="summary-label">Year 1 Revenue</span>
                    <span className="summary-value">{formatCurrency(scenarios[11]?.grossRevenue || 0)}</span>
                  </div>
                </div>
                <div className="summary-card">
                  <TrendingUp size={20} />
                  <div>
                    <span className="summary-label">Year 1 Net</span>
                    <span className={`summary-value ${(scenarios[11]?.netProfit || 0) >= 0 ? 'positive' : 'negative'}`}>
                      {formatCurrency(scenarios[11]?.netProfit || 0)}
                    </span>
                  </div>
                </div>
                <div className="summary-card">
                  <PieChart size={20} />
                  <div>
                    <span className="summary-label">Profit Margin</span>
                    <span className="summary-value">
                      {scenarios[11]?.grossRevenue > 0 
                        ? `${((scenarios[11].netProfit / scenarios[11].grossRevenue) * 100).toFixed(1)}%`
                        : '0%'
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PackagePrediction;
